import React, { useContext, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

import { Contact, Profile, RecordingType } from '~/types/models';
import ActionLinks, { ActionLink } from './ActionLinks';
import QuickShareModal from './VideoShare/QuickShareModal';

import DeleteConfirmationModal from '~/App/Components/Modals/DeleteConfirmationModal';
import { useQueryClient } from 'react-query';
import EditNameModal from '../../Testimonials/Components/EditNameModal';
import ThumbnailPickerModal from '../../Testimonials/Components/ThumbnailPicker/ThumbnailPickerModal';
import { ThumbnailArgs } from '~/App/Components/Record/Stores/Recorder/Thumbnail';
import { getThumbnailBlob } from '~/utils/thumbnail';
import ManageContactModal from '../../ManualUpload/Components/ManageContactModal';
import { useAssignResourceContact } from '../Api/ResourceContacts';
import { TaggableResourceType } from '~/types/models/tags';
import RemoveContactModal from './RemoveContactModal';
import { useRouteMatch } from 'react-router';
import { TestimonialAssetPage } from '~/types/models/testimonial';
import { ShareLibraryAssetTab } from '../../ShareLibrary/util';
import {
  EditableThumbnailResourcePage,
  EditableThumbnailResourceType,
} from '~/types/models/thumbnail';
import {
  UserAsset,
  useDeleteUserAsset,
  useUpdateUserAsset,
  userAssetsApiUrl,
  userAssetsQueryKeys,
} from '../Api/UserAsset';
import EditTextAssetModal from '~/App/Components/Modals/AddTextAssetModal';
import { getRichTextFromSlateData, RichText } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import axios from 'axios';
import {
  TestimonialAssetEditorModelType,
  openTestimonialAssetEditorModal,
} from '../../Testimonials/Components/TestimonialAssetEditorModal';
import FlashContext from '~/App/Components/Flash/Context';
import useResourceTagging from '../../Tags/hooks/useResourceTagging';

export interface UserAssetActionLinksProps {
  userAsset: UserAsset;
  openManageContactModal: boolean;
  onRequestOpenManageContactModal: () => void;
  onRequestCloseManageContactModal: () => void;
  hideContactTagging?: boolean;
  showDownloadLink?: boolean;
}

export default function UserAssetActionLinks({
  userAsset,
  openManageContactModal,
  onRequestOpenManageContactModal,
  onRequestCloseManageContactModal,
  hideContactTagging = false,
  showDownloadLink = false,
}: UserAssetActionLinksProps) {
  const { createFlash } = useContext(FlashContext);
  const [showQuickShareModal, setShowQuickShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [showThumbnailPickerModal, setShowThumbnailPickerModal] =
    useState(false);
  const [showRemoveContactModal, setShowRemoveContactModal] = useState(false);
  const [content, setContent] = useState<RichText>();

  const queryClient = useQueryClient();
  const { defaultTags } = useResourceTagging(userAsset);

  const closeRenameModal = () => setShowRenameModal(false);
  const closeThumbnailPickerModal = () => setShowThumbnailPickerModal(false);
  const closeDeleteConfirmationModal = () =>
    setShowDeleteConfirmationModal(false);
  const closeRemoveContactModal = () => setShowRemoveContactModal(false);
  const closeEditModal = () => setShowEditModal(false);

  const testimonialPage = useRouteMatch<{ assetPage?: TestimonialAssetPage }>();
  const { assetPage: testimonialLibraryPage } = testimonialPage.params;

  const match = useRouteMatch<{ tab?: ShareLibraryAssetTab }>();
  const { tab: shareLibraryAssetTab } = match.params;

  const { editableThumbnailResourceType, editableThumbnailResourcePage } =
    useMemo<{
      editableThumbnailResourcePage: string;
      editableThumbnailResourceType: string;
    }>(() => {
      const isTestimonial =
        userAsset?.video?.type === RecordingType.Testimonial;
      const editableThumbnailResourceType =
        userAsset?.video?.type === RecordingType.Upload || isTestimonial
          ? EditableThumbnailResourceType.Uploaded
          : EditableThumbnailResourceType.Recordings;

      let editableThumbnailResourcePage = '';

      if (isTestimonial) {
        if (testimonialLibraryPage === TestimonialAssetPage.All) {
          editableThumbnailResourcePage =
            EditableThumbnailResourcePage.TestimonialAll;
        } else {
          editableThumbnailResourcePage =
            EditableThumbnailResourcePage.TestimonialUploads;
        }
      } else {
        switch (shareLibraryAssetTab) {
          case ShareLibraryAssetTab.All:
            editableThumbnailResourcePage =
              EditableThumbnailResourcePage.ShareLibraryAll;
            break;
          case ShareLibraryAssetTab.Recordings:
            editableThumbnailResourcePage =
              EditableThumbnailResourcePage.ShareLibraryRecordings;
            break;
          case ShareLibraryAssetTab.Uploads:
          default:
            editableThumbnailResourcePage =
              EditableThumbnailResourcePage.ShareLibraryUploaded;
            break;
        }
      }
      return { editableThumbnailResourcePage, editableThumbnailResourceType };
    }, [userAsset, testimonialLibraryPage, shareLibraryAssetTab]);

  const invalidateQuery = () => {
    void queryClient.invalidateQueries(userAssetsQueryKeys.all);
  };

  const { mutateAsync: updateUserAsset, isLoading: updating } =
    useUpdateUserAsset({
      onSuccess: () => {
        closeRenameModal();
        closeThumbnailPickerModal();
        closeRenameModal();
        closeEditModal();
        invalidateQuery();
      },
    });

  const { mutateAsync: deleteUserAsset, isLoading: userAssetDeleting } =
    useDeleteUserAsset({
      onSuccess: () => {
        invalidateQuery();
        closeDeleteConfirmationModal();
      },
    });

  const { mutateAsync: assignResourceContactMutation, isLoading: isAssigning } =
    useAssignResourceContact({
      options: {
        onSuccess: () => {
          onRequestCloseManageContactModal();
          return invalidateQuery();
        },
      },
    });

  const handleOnDelete = async () => {
    await deleteUserAsset(userAsset);
  };

  const handleOnRename = async (name: string) => {
    await updateUserAsset({ uuid: userAsset?.uuid, name });
  };

  const handleOnThumbnailSelected = async (thumbnail: ThumbnailArgs) => {
    const thumbnailBlob = await getThumbnailBlob(thumbnail.original);
    const formData = new FormData();
    formData.append('thumbnail', thumbnailBlob, 'thumbnail.jpg');
    formData.append('_method', 'PUT');

    await axios.post<UserAsset>(
      `${userAssetsApiUrl}/${userAsset.uuid}?single_upload=true`,
      formData
    );

    invalidateQuery();
  };

  const handleOnContactAssign = (contact: Contact) => {
    void assignResourceContactMutation({
      profileUuid: contact.id,
      resourceType: TaggableResourceType.UserAsset,
      resourceUuid: userAsset.uuid,
    });
  };

  const handleOnEdit = () => {
    void updateUserAsset({ uuid: userAsset?.uuid, content_rich: content });
  };

  const handleOnContactSave = () => {
    onRequestCloseManageContactModal();
    void invalidateQuery();
  };

  const defaultLinkClass = cx('hover:!v2-bg-yellow-100');

  const defaultLinkLabelClass = cx('v2-bg-neutral-900');

  const deleteButtonClass = cx('v2-text-orange-700');

  const deleteButtonContainerClass = cx('hover:!v2-bg-magenta-50');

  const links = useMemo<ActionLink[]>(() => {
    const hasContact = userAsset.author !== null;

    const contactActionLinks: ActionLink[] = [];

    if (!hideContactTagging) {
      contactActionLinks.push({
        label: (
          <>
            <i
              className={cx('icon icon-sm', defaultLinkLabelClass, {
                'icon-add-contact': !hasContact,
                'icon-edit-contact': hasContact,
              })}
            />{' '}
            <span className='text-sm'>
              {hasContact ? 'Edit profile' : 'Add a profile'}
            </span>
          </>
        ),
        onClick: onRequestOpenManageContactModal,
        className: defaultLinkClass,
      });

      if (hasContact) {
        contactActionLinks.push({
          label: (
            <>
              <i
                className={cx(
                  'icon icon-sm icon-remove-contact',
                  defaultLinkLabelClass
                )}
              />{' '}
              <span className='text-sm'>Remove profile</span>
            </>
          ),
          onClick: () => setShowRemoveContactModal(true),
          className: defaultLinkClass,
        });
      }
    }

    const downloadActionLinks: ActionLink[] = [];

    if (userAsset?.video != null && showDownloadLink) {
      downloadActionLinks.push({
        label: (
          <>
            <i
              className={cx(
                'icon icon-sm icon-download',
                defaultLinkLabelClass
              )}
            />{' '}
            <span className='text-sm'>Download</span>
          </>
        ),
        onClick: () =>
          window.open(
            userAsset?.video?.video_url,
            '_blank',
            'noopener,noreferrer'
          ),
        className: defaultLinkClass,
      });
    }

    return [
      ...(userAsset?.video != null
        ? [
            {
              label: (
                <>
                  <i className='far fa-share-alt' />{' '}
                  <span className='text-sm'>Share video</span>
                </>
              ),
              onClick: () => setShowQuickShareModal(true),
              className: defaultLinkClass,
            },
          ]
        : []),
      ...contactActionLinks,
      ...(userAsset?.video != null
        ? [
            {
              label: (
                <>
                  <i className='fa fa-image' />{' '}
                  <span className='text-sm'>Edit thumbnail</span>
                </>
              ),
              onClick: () => setShowThumbnailPickerModal(true),
              className: defaultLinkClass,
            },
          ]
        : []),
      {
        label: (
          <>
            <i
              className={cx('icon icon-sm icon-text', defaultLinkLabelClass)}
            />{' '}
            <span className='text-sm'>Rename</span>
          </>
        ),
        onClick: () => setShowRenameModal(true),
        className: defaultLinkClass,
      },
      {
        label: (
          <>
            <i className='fa fa-edit' /> <span className='text-sm'>Edit</span>
          </>
        ),
        onClick: () =>
          openTestimonialAssetEditorModal({
            type: TestimonialAssetEditorModelType.UserAssets,
            title: 'Edit testimonial',
            id: userAsset.uuid,
            profile: userAsset?.author as Contact,
            videoUrl: userAsset?.video?.video_url,
            text: userAsset?.content_rich,
            transcript: userAsset?.video?.transcript,
            tags: defaultTags,
            modelProperties: {
              userAssetUuid: userAsset?.uuid,
            },
            successCallback: () => {
              createFlash({
                id: 'testimonial-asset-edit-update-success',
                title: 'Success',
                message: 'Testimonial updated!',
              });
            },
          }),
        className: defaultLinkClass,
      },
      ...downloadActionLinks,
      {
        label: (
          <>
            <i className={`fa fa-trash ${deleteButtonClass}`} />{' '}
            <span className={`text-sm  ${deleteButtonClass}`}>Delete</span>
          </>
        ),
        onClick: () => setShowDeleteConfirmationModal(true),
        className: deleteButtonContainerClass,
      },
    ];
  }, [userAsset]);

  useEffect(() => {
    if (userAsset.content_rich) {
      setContent(getRichTextFromSlateData(userAsset.content_rich));
    }
  }, [userAsset, showEditModal]);

  return (
    <>
      <ActionLinks links={links} />
      {userAsset?.video && (
        <>
          <QuickShareModal
            show={showQuickShareModal}
            onModalClose={() => setShowQuickShareModal(false)}
            recording={userAsset?.video}
          />
          <ThumbnailPickerModal
            open={showThumbnailPickerModal}
            onClose={closeThumbnailPickerModal}
            videoUrl={userAsset?.video?.video_url}
            onSave={handleOnThumbnailSelected}
            saving={updating}
            resourcePage={editableThumbnailResourcePage}
            resourceType={editableThumbnailResourceType}
          />
        </>
      )}
      <EditTextAssetModal
        textAreaClassName={cx({ '!border-red': !content })}
        heading='Edit text'
        open={showEditModal}
        onRequestClose={closeEditModal}
        content={content}
        onSetContent={setContent}
        onSave={handleOnEdit}
        loading={updating}
      />
      <EditNameModal
        heading='Name your video'
        open={showRenameModal}
        onRequestClose={closeRenameModal}
        onConfirmed={handleOnRename}
        defaultValue={userAsset?.name}
        loading={updating}
        confirmButtonText='Rename'
        showCancelButton
      />
      <DeleteConfirmationModal
        open={showDeleteConfirmationModal}
        title={`Are you sure you want to delete ${
          userAsset?.name ?? 'this recording'
        }?`}
        onConfirm={handleOnDelete}
        onCancel={closeDeleteConfirmationModal}
        loading={userAssetDeleting}
      />
      <ManageContactModal
        open={openManageContactModal}
        onRequestClose={onRequestCloseManageContactModal}
        initialValue={userAsset?.author as Contact}
        onContactSave={handleOnContactSave}
        onContactAssign={handleOnContactAssign}
        loading={isAssigning}
      />
      {userAsset?.author && (
        <RemoveContactModal
          open={showRemoveContactModal}
          profileUuid={(userAsset?.author as Profile).id}
          resourceUuid={userAsset?.uuid}
          resourceType={TaggableResourceType.UserAsset}
          onModalClose={closeRemoveContactModal}
          onSuccess={invalidateQuery}
        />
      )}
    </>
  );
}
